import { CreditCostMarker } from "@/components/features/Credits/CreditCostMarker";
import { Button } from "@/design-system/Button/Button";
import { Dialog } from "@/design-system/Dialog/Dialog";
import { Dropdown } from "@/design-system/Dropdown/Dropdown";
import Input from "@/design-system/Input/Input";
import { SegmentedControl } from "@/design-system/SegmentedControl/SegmentedControl";
import BackButton from "@/design-system/SpecificButtons/BackButton/BackButton";
import { trackClubImportCta } from "@/events/clubImportMixpanel";
import {
  formationsSelector,
  loadedContentSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import { showModal } from "@/redux/message/messageSlice";
import {
  defaultSquadBuilderSelector,
  lockedFormationForLiveEvent,
  selectLiveEvent,
} from "@/redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import { Scope } from "@/redux/squadBuilder/squadBuilderUtils";
import { AppDispatch } from "@/redux/store";
import { userIsLoggedInSelector } from "@/redux/user/userSlice";
import { Trans, useTranslation } from "@repo/shared/i18n";
import {
  featureActionPhrase,
  noteClubImportIntent,
  usesClubPool,
} from "@repo/shared/redux";
import { ReactNode, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { CoinIcon, both, club, market } from "../../../../../designSystemIcons";
import { LIVE_EVENT_SHORT_NAMES } from "../../RightPanel/InputMode/InputMode";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Build with whatever is set, including nothing. */
  onBuild: () => void;
  selectedFormation: string;
  handleUpdateFormation: (formationId: string, isTemp: boolean) => void;
  budget: number;
  handleBudgetUpdated: (budget: number) => void;
  scope: Scope;
  handleScopeChange: (scope: Scope) => void;
  /** Whether a club is there to build from; without one the scope tab flips to the import page. */
  canUseClub: boolean;
};

type Page = "customize" | "club-gate";

// Literal keys: Trans's typed i18nKey will not take a template string.
const BUDGET_LINE_KEYS = {
  scopeMarket: "customize.scopeMarket",
  scopeBoth: "customize.scopeBoth",
} as const;

const CLUB_IMPORT_ORIGIN = { section: "Squad Builder" } as const;
const CLUB_FEATURE_ID = "build-squad-club" as const;
/* The live event dropdown's "none" entry. */
const NO_LIVE_EVENT = "none";

const SCOPE_ICONS: Record<Scope, (active: boolean) => ReactNode> = {
  market,
  both,
  club,
};

/** Label on the left, control on the right; the dialog's one row shape. */
const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="text-sm text-foreground-muted">{label}</span>
    <div className="w-[190px]">{children}</div>
  </div>
);

/**
 * Raised on a Build Squads click while every filter is still at its default
 * (build-first arm of the squad builder filters test): a last chance to say
 * what the squads should be built from before spending the build.
 *
 * Two pages in one surface. Picking a club scope without a club swaps the
 * body for the club-import gate (same copy as ClubImportGateDialog) with a
 * back arrow, instead of stacking a second dialog under this one.
 */
export const CustomizeSquadDialog = ({
  isOpen,
  onClose,
  onBuild,
  selectedFormation,
  handleUpdateFormation,
  budget,
  handleBudgetUpdated,
  scope,
  handleScopeChange,
  canUseClub,
}: Props) => {
  const { t } = useTranslation(["squadBuilder", "clubImport", "common"]);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const formations = useSelector(formationsSelector);
  const { selectedLiveEventId } = useSelector(defaultSquadBuilderSelector);
  const lockedFormation = lockedFormationForLiveEvent(selectedLiveEventId);
  const isLoggedIn = useSelector(userIsLoggedInSelector);
  const [page, setPage] = useState<Page>("customize");
  // A market build with no budget asks once more before it goes.
  const [confirming, setConfirming] = useState(false);

  // The gate page is shorter; it borrows the customize page's height so the
  // surface does not jump when the body swaps.
  const customizeRef = useRef<HTMLDivElement>(null);
  const [bodyHeight, setBodyHeight] = useState<number>();
  useLayoutEffect(() => {
    if (page === "customize" && customizeRef.current)
      setBodyHeight(customizeRef.current.offsetHeight);
  }, [page, confirming, isOpen]);

  // The live event, as in the squad builder's panel: one squad for its rules.
  const { liveEvents } = useSelector(loadedContentSelector);
  const liveEventItems = useMemo(
    () => [
      { value: NO_LIVE_EVENT, label: t("squadBuilder:input.noLiveEvent") },
      ...liveEvents.map((e) => ({
        value: String(e.id),
        label: LIVE_EVENT_SHORT_NAMES[e.name] ?? e.name,
      })),
    ],
    [liveEvents, t],
  );

  const formationItems = useMemo(
    () =>
      Object.keys(formations)
        .filter((id) => !lockedFormation || id === lockedFormation)
        .map((id) => ({
          value: id,
          label: formations[id].label,
        })),
    [formations, lockedFormation],
  );

  const scopeOptions = useMemo(
    () =>
      (["market", "both", "club"] as const).map((value) => ({
        value,
        label: (
          <span className="flex items-center gap-1.5">
            <span className="size-5">
              {SCOPE_ICONS[value](value === scope)}
            </span>
            {t(`squadBuilder:customize.tab.${value}`)}
          </span>
        ),
      })),
    [scope, t],
  );

  const hasBudget = budget !== undefined && budget !== Number.MAX_VALUE;
  const budgetDisplay = hasBudget ? budget.toLocaleString("en-US") : "";
  const needsBudgetConfirm = scope !== "club" && !hasBudget;
  const showWarning = confirming && needsBudgetConfirm;

  // The budget lines carry the coin inline, so they go through Trans.
  const budgetLine = (key: keyof typeof BUDGET_LINE_KEYS) => (
    <Trans
      ns="squadBuilder"
      i18nKey={BUDGET_LINE_KEYS[key]}
      values={{ budget: budgetDisplay }}
      components={{
        coin: (
          <span className="mr-0.5 inline-block align-[-2px]">
            <CoinIcon size={12} />
          </span>
        ),
      }}
    />
  );
  const scopeDescription =
    scope === "club"
      ? t("squadBuilder:customize.scopeClub")
      : scope === "both"
        ? hasBudget
          ? budgetLine("scopeBoth")
          : t("squadBuilder:customize.scopeBothNoBudget")
        : hasBudget
          ? budgetLine("scopeMarket")
          : t("squadBuilder:customize.scopeMarketNoBudget");

  const close = () => {
    setConfirming(false);
    setPage("customize");
    onClose();
  };

  const build = () => {
    if (needsBudgetConfirm && !confirming) {
      setConfirming(true);
      return;
    }
    onBuild();
    close();
  };

  const pickScope = (next: Scope) => {
    if (next !== "market" && !canUseClub) {
      // Same bookkeeping promptClubImport does when it raises the gate.
      noteClubImportIntent(CLUB_IMPORT_ORIGIN, CLUB_FEATURE_ID);
      setPage("club-gate");
      return;
    }
    handleScopeChange(next);
  };

  const startImport = () => {
    trackClubImportCta(CLUB_IMPORT_ORIGIN);
    close();
    navigate("/club-import-tutorial");
  };

  // Both titles sit in the back button's row height, so the surface stays the
  // same size whether or not the arrow is there.
  // -mt-1.5 mirrors the offset Dialog gives its close button, so arrow, text
  // and X share one centre line; -ml-1.5 hugs the arrow to the edge like the X.
  const title = (
    <span className="-mt-1.5 flex min-h-8 items-center gap-2">
      {page === "club-gate" && (
        <span className="-ml-1.5">
          <BackButton onClick={() => setPage("customize")} />
        </span>
      )}
      <span>
        {page === "club-gate"
          ? t("clubImport:gate.title")
          : t("squadBuilder:customize.title")}
      </span>
    </span>
  );

  // Portaled: the squad builder's pitch band is a z-indexed stacking context
  // that would otherwise draw over the dialog. The wrapper lifts it above the
  // mobile floating Build button (z-100).
  return createPortal(
    <div className="relative z-[110]">
      <Dialog
        isOpen={isOpen}
        onClose={close}
        title={title}
        negLabel=""
        posLabel=""
      >
        {page === "customize" ? (
          <div ref={customizeRef} className="mt-2 flex flex-col gap-y-4">
            <Row label={t("squadBuilder:input.formation")}>
              <Dropdown
                items={formationItems}
                selected={selectedFormation}
                placeholder={t("squadBuilder:input.formation")}
                onChange={(item) => handleUpdateFormation(item.value, false)}
                variant="secondary"
                hideSearch
                width={190}
                align="end"
              />
            </Row>
            {liveEvents.length > 0 && (
              <Row label={t("squadBuilder:input.liveEvent")}>
                <Dropdown
                  items={liveEventItems}
                  selected={
                    selectedLiveEventId === undefined
                      ? NO_LIVE_EVENT
                      : String(selectedLiveEventId)
                  }
                  placeholder={t("squadBuilder:input.liveEvent")}
                  onChange={(item) =>
                    dispatch(
                      selectLiveEvent(
                        item.value === NO_LIVE_EVENT
                          ? undefined
                          : Number(item.value),
                      ),
                    )
                  }
                  variant="secondary"
                  hideSearch
                  width={190}
                  align="end"
                />
              </Row>
            )}
            <Row label={t("squadBuilder:input.budget")}>
              <Input
                size="small"
                className="text-sm"
                inputMode="numeric"
                icon={<CoinIcon size={16} />}
                placeholder={t("squadBuilder:input.budgetPlaceholder")}
                value={budgetDisplay}
                onChange={(value) => {
                  const digits = value.replace(/[^\d]/g, "");
                  handleBudgetUpdated(
                    digits === "" ? Number.MAX_VALUE : Number(digits),
                  );
                }}
              />
            </Row>
            <div className="flex flex-col items-center gap-y-2">
              <SegmentedControl
                label={t("squadBuilder:customize.scopeLabel")}
                variant="primary"
                track="page"
                value={scope}
                onChange={pickScope}
                options={scopeOptions}
                className="w-full [&>button]:flex-1 [&>button]:justify-center"
              />
              <span className="text-xs text-foreground-muted">
                {scopeDescription}
              </span>
            </div>
            {showWarning && (
              <p className="text-center text-sm text-foreground-muted">
                {t("squadBuilder:customize.noBudgetWarning")}
              </p>
            )}
            {/* The price, where the build costs one: club players only. */}
            <div className="flex items-center gap-3">
              <CreditCostMarker
                featureId={CLUB_FEATURE_ID}
                when={usesClubPool(scope)}
              />
              <div className="flex-1">
                <Button variant="primary" size="medium" stretch onClick={build}>
                  {showWarning
                    ? t("squadBuilder:customize.confirmAndBuild")
                    : t("squadBuilder:build.buildSquads")}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="mt-2 flex flex-col justify-between gap-y-4"
            style={{ minHeight: bodyHeight }}
          >
            <div>
              <p>
                {t("clubImport:gate.body", {
                  action: featureActionPhrase(CLUB_FEATURE_ID),
                })}
              </p>
              {!isLoggedIn && (
                <p className="mt-3 text-xs">
                  {t("clubImport:gate.alreadyImported")}{" "}
                  <button
                    className="underline underline-offset-2"
                    onClick={() => {
                      close();
                      dispatch(showModal({ modalType: "login-required" }));
                    }}
                  >
                    {t("common:logIn")}
                  </button>
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2.5">
              <Button
                variant="secondary"
                size="medium"
                className="px-5 whitespace-nowrap"
                onClick={() => setPage("customize")}
              >
                {t("clubImport:gate.maybeLater")}
              </Button>
              <Button
                variant="primary"
                size="medium"
                stretch
                onClick={startImport}
              >
                {t("clubImport:gate.cta")}
              </Button>
            </div>
          </div>
        )}
      </Dialog>
    </div>,
    document.body,
  );
};
